import {
    Box,
    Button,
    Container,
    Fab,
    Link,
    ListItemText,
    Skeleton,
    Stack,
} from '@mui/material'
import {
    createSearchParams,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import { useInnsynContext } from '../context/InnsynContextProvider'
import { Favorite, FmdGoodOutlined } from '@mui/icons-material'
import { INNSYN_SEARCH_OEREMERKE_INFO_PATH } from '../paths'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import colors from '../styles/colors.scss'
import { getOmraadeNameKey } from '../services/TextService'

export const EarTagListItem = ({ id, navn }: { id: number; navn?: string }) => {
    const [isFavorite, setIsFavorite] = useState<boolean>(false)
    const navigate = useNavigate()
    const intl = useIntl()
    const [query] = useSearchParams()
    const { merker, geografi, isLoadingMerke, isLoadingGeografi } =
        useInnsynContext()

    const merke = merker?.find((el) => el.id === id)

    const tagInfo = (id: number) => {
        sessionStorage.setItem('scrollPosition', window.scrollY.toString())
        navigate({
            pathname: INNSYN_SEARCH_OEREMERKE_INFO_PATH(id),
            search: `?${createSearchParams(query)}`,
        })
    }

    const findOmraade = (geografi: Area[], merke: Merke) => {
        return geografi.find((omraade) => merke.omraadeId === omraade.omraadeId)
    }

    const findDistrikt = (geografi: Area[], merke: Merke) => {
        return geografi
            .find((omraade) => merke.omraadeId === omraade.omraadeId)
            ?.distrikter.find(
                (distrikt) => distrikt.distriktId === merke.distriktId
            )
    }

    useEffect(() => {
        const currentStorage = localStorage.getItem(
            'reinmerker-innsyn-favoritter-v1'
        )
        if (id && currentStorage) {
            setIsFavorite(currentStorage.includes(id.toString()))
        }
    }, [id])

    const handleFavoriteStateChange = () => {
        if (isFavorite) {
            removeMerkeFromFavorites()
        } else {
            addMerkeToFavorites()
        }
    }

    const addMerkeToFavorites = () => {
        const currentStorage = localStorage.getItem(
            'reinmerker-innsyn-favoritter-v1'
        )
        const newStorage = `${currentStorage ? currentStorage : ''}${
            currentStorage && currentStorage?.length > 0 ? '&' : ''
        }${id}`
        localStorage.setItem('reinmerker-innsyn-favoritter-v1', newStorage)
        setIsFavorite(true)
    }

    const removeMerkeFromFavorites = () => {
        const currentStorage = localStorage.getItem(
            'reinmerker-innsyn-favoritter-v1'
        )
        if (!currentStorage) return
        if (id && currentStorage.includes(id.toString())) {
            const allFavorites = currentStorage.split('&')
            let newStorage = ''
            // If in middle or last item
            if (
                allFavorites.length > 1 &&
                !allFavorites[0].includes(id.toString())
            ) {
                newStorage = currentStorage.replace(`&${id}`, '')
                // If the very first item
            } else if (allFavorites.length > 1) {
                newStorage = currentStorage.replace(`${id}&`, '')
                // If the only item
            } else {
                newStorage = currentStorage.replace(`${id}`, '')
            }
            localStorage.setItem('reinmerker-innsyn-favoritter-v1', newStorage)
            setIsFavorite(false)
        }
    }

    return (
        <Container>
            <Box>
                {merke && !isLoadingMerke && !isLoadingGeografi ? (
                    <Box sx={{ width: '100%' }}>
                        <img
                            onClick={() => tagInfo(merke.id)}
                            style={{
                                width:
                                    window.screen.width > 600
                                        ? '30rem'
                                        : '100%',
                                marginTop: '0.5rem',
                            }}
                            alt='Reinmerke-bilde'
                            src={`data:image/gif;base64,${merke.bilde}`}
                        />
                        <ListItemText
                            sx={{ marginTop: 0 }}
                            primary={
                                <Stack
                                    direction='row'
                                    justifyContent='space-between'
                                    sx={{ alignItems: 'flex-end' }}>
                                    <Link
                                        onClick={() => tagInfo(merke.id)}
                                        tabIndex={1}
                                        color='inherit'
                                        sx={{
                                            width: 'fit-content',
                                            marginBottom: '0.5rem',
                                            marginRight: '1rem',
                                        }}>
                                        <Box>
                                            <FormattedMessage
                                                id={
                                                    'component.ear-tag-list-item.merkenummer'
                                                }
                                                values={{ num: merke.merkenr }}
                                            />
                                        </Box>
                                    </Link>
                                    <Button
                                        onClick={() =>
                                            handleFavoriteStateChange()
                                        }
                                        aria-label='Legg til i favoritter'>
                                        <Fab
                                            sx={{
                                                boxShadow: 'none',
                                            }}
                                            size='small'
                                            //@ts-ignore
                                            color={
                                                isFavorite
                                                    ? 'primary'
                                                    : 'neutral'
                                            }
                                            aria-label='Legg til i favoritter'>
                                            <Favorite
                                                sx={{
                                                    color: 'common.white',
                                                }}
                                            />
                                        </Fab>
                                    </Button>
                                </Stack>
                            }
                            secondary={
                                <Stack
                                    direction='row'
                                    sx={{ marginTop: '0.25rem' }}>
                                    <FmdGoodOutlined
                                        sx={{
                                            color: colors.iconGray,
                                            marginRight: '0.5rem',
                                        }}
                                    />
                                    <Stack
                                        direction='row'
                                        sx={{
                                            alignItems: 'flex-end',
                                            fontSize: '1rem',
                                            width: '20rem',
                                        }}>
                                        {geografi &&
                                            intl.formatMessage({
                                                id: getOmraadeNameKey(
                                                    findOmraade(geografi, merke)
                                                        ?.omraadeId
                                                ),
                                            })}
                                        ,{' '}
                                        {geografi &&
                                            findDistrikt(geografi, merke)
                                                ?.distriktNavn}{' '}
                                    </Stack>
                                </Stack>
                            }
                        />

                        {navn && (
                            <Box
                                sx={{
                                    textTransform: 'uppercase',
                                    alignItems: 'flex-end',
                                    fontSize: '1rem',
                                    marginLeft: '2rem',
                                }}>
                                {navn}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Skeleton animation='wave' width='350px' height='210px' />
                )}
            </Box>
        </Container>
    )
}

export default EarTagListItem
